import * as React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { isInteger } from "lodash-es";
import {
  ButtonWithLoader,
  InputBaseContainer,
  InputDatePickerWithLabel,
  // InputCurrencyWithLabel,
  InputSelectWithLabel,
  InputTextWithLabel,
  ModalDefaultDraggable,
  TextAreaWithLabel,
} from '../molecules';
import ContactsTable from '../organisms/ContactsTable';
import ContactSearchList from '../organisms/ContactSearchList';
import ActionsPanel from '../organisms/ActionsPanel';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import useTablePage from '../hooks/useTablePage';
import { createPermit } from '../api/permits';
import { ButtonMain } from '../atoms';
import { fontFamily } from '../theme';
import ModalActionDialog from '../molecules/ModalActionDialog';
import PermitAddContact from '../organisms/PermitAddContact';
import ModalInfo from '../molecules/ModalInfo';
import { getUsersCustom } from '../api/users';
import { DEFAULT_DATE_FORMAT } from '../constants';
import { USER_TYPES_VALUES, CLIENT_TYPES_VALUES } from '../constants/forms';
import formatDate from '../utils/formatDate';

const defaultValues = {
  name: '',
  permitManager: '',
  contacts: '',
  description: '',
  contractor: '',
  inspectionsNumber: '',
  totalContract: '',
  serviceType: '',
  permitType: '',
  work: '',
  gateCode: '',
  folioId: '',
  permitCost: '',
};

const labels = {
  name: 'Name',
  number: 'Permit Number',
  total: 'Contract Total',
  square: 'Square Feet',
  inspections: 'Count of Inspections',
  storageBox: 'Storage Box Number',
  jurisdiction: 'Jurisdiction',
  documents: 'Permit Supporting Documents',
  address: 'Address',
  description: 'Description',
  info: 'Permit Info',
  gateCode: 'Gate Code',
  folioId: 'Folio/Property ID',
  permitCost: 'Permit Cost',
  status: 'Permit Status',
  permitContacts: 'Contacts',
  serviceType: 'Type of Service',
  manager: 'Project Manager',
  work: 'Work',
  contractor: 'Contractor',
  expirationDate: 'Expiration Date',
};

const validationSchema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  description: yup
    .string()
    .required()
    .label(labels.description),
  contractor: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .nullable()
    .label(labels.contractor),
    inspectionsNumber: yup
    .number()
    .transform((cv, ov) => {
      return ov === '' ? undefined : cv;
    })
    /* .positive() */
    .typeError('Count of inspections must be a number')
    .nullable()
    .label(labels.inspections),
  totalContract: yup
    .string()
    .label(labels.total)
    .nullable(),
  serviceType: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .required()
    .typeError('Select a Service Types from the list')
    .nullable()
    .label(labels.serviceType),
  permitManager: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Project Manager from the list')
    .nullable()
    .label(labels.manager),
  work: yup
    .object()
    .shape({
      value: yup.string(),
      label: yup.string(),
    })
    .required()
    .typeError('Select a Scope of Work from the list')
    .nullable()
    .label(labels.work),
  contacts: yup
    .array()
    .of(yup.object())
    .required()
    .typeError('Select permit contacts'),
});

const AddPermitForm = ({
  allContacts,
  serviceTypes,
  projectBuilding,
  projectManager,
  workTypes,
  contactTypes,
  managersData,
  setIsContactFormSubmitting,
  selectedProjectId,
  close,
  setCreatedPermit,
}: Props) => {
  const {
    createOrUpdate,
    modalSuccess,
    modalErrorTitle,
    modalError,
    search,
    setSearch,
  } = useTablePage({
    createEndpoint: createPermit,
  });
  const [
    isAddContactModal,
    setAddContactModal,
  ] = React.useState(false);
  const [
    addContactModalTitle,
    setAddContactModalTitle,
  ] = React.useState('Add Individual');

  // The permit's project manager is inherited from the project it belongs to —
  // NOT from whoever happens to be creating the permit.
  const [
    inheritedManager,
    setInheritedManager,
  ] = React.useState(null);

  const [allManagers, setAllManagers] = React.useState(null);

  const projectManagerId = projectManager?.id ?? projectManager;

  React.useEffect(() => {
    getUsersCustom({
      userRole: USER_TYPES_VALUES.EMPLOYEE,
    }).then(projectManagersResponse => {
      const activeManagers = projectManagersResponse.data;
      setAllManagers(activeManagers);
      setInheritedManager(
        activeManagers.find(
          el => el.id === projectManagerId,
        ) || null,
      );
    });
  }, [projectManagerId]);

  React.useEffect(() => {
    if (modalSuccess && !modalError) {
      setCreatedPermit(true);
      close();
    }
  }, [modalSuccess]);

  const {
    handleSubmit,
    errors,
    control,
    setValue,
    getValues,
    formState,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });
  React.useEffect(() => {
    if (!contactTypes || !contactTypes.length) {
      setError('contacts', {
        type: 'manual',
        message:
          'There are no Contact Types in the system. Please contact Elite Permits support.',
      });
    }
  }, [isAddContactModal]);

  React.useEffect(() => {
    const managerValue = getValues('permitManager');
    if (inheritedManager && !managerValue) {
      setValue('permitManager', inheritedManager);
    }
  }, [inheritedManager]);

  const [
    selectedContacts,
    setSelectedContacts,
  ] = React.useState([]);

  const [savedContacts, setSavedContacts] = React.useState(
    null,
  );

  const [
    isContactModalOpen,
    setContactModalOpen,
  ] = React.useState(false);

  React.useEffect(() => {
    if (
      (savedContacts || selectedContacts) &&
      contactTypes &&
      contactTypes?.length
    ) {
      clearErrors('contacts');
    }
    setValue('contacts', savedContacts as any);
  }, [selectedContacts, savedContacts]);

  const isInitialContactModal = React.useRef(true);
  React.useEffect(() => {
    if (isInitialContactModal.current) {
      isInitialContactModal.current = false;
      return;
    }
    // `search` is global Redux state (filters.searchToken), shared with the
    // Wiki/Search pages — reset it on every open and close so the query never
    // leaks out of this modal.
    setSearch('');
    if (isContactModalOpen) {
      setSelectedContacts(savedContacts || []);
    }
  }, [isContactModalOpen]);

  // const onCurrencyChange = (value, name) => {
  //   setValue(name, value, { shouldDirty: true });
  // };

  const formatContacts = contacts => {
    const getBooleanValue = value => value === true || value === 1 || value === 'true';
    if (contacts) {
      return contacts
        ?.map(contact => {
          const id = +contact.id;
          const isCompany = Number(contact?.clientType) === CLIENT_TYPES_VALUES.COMPANY;
          return {
            ...(isCompany ? { company_user: id } : { user: id }),
            contact_type: contact.contactType
              ? +contact.contactType
              : contactTypes[0]?.id,
            isInspection: getBooleanValue(contact.isInspection),
          };
        })
        .filter(el => !!(el.user || el.company_user));
    }
    return [];
  };

  // `selectedContacts` is the modal's working draft: seeded from what's already
  // on the permit so unchecking a row removes it, committed to `savedContacts`
  // (the ContactsTable below) only on Ok.
  const handleToggleContact = contact => {
    setSelectedContacts(current => {
      const draft = current || [];
      return draft.some(el => el?.id === contact.id)
        ? draft.filter(el => el?.id !== contact.id)
        : [...draft, contact];
    });
  };

  const handleSaveContacts = () => {
    const draft = selectedContacts || [];
    const uniqueContacts = draft.filter(
      (el, i, arr) =>
        el !== undefined &&
        arr.findIndex(other => other?.id === el?.id) === i,
    );
    // Commit null rather than [] when nothing is selected: yup's
    // `array().required()` accepts an empty array, so [] would let a permit
    // save with no contacts at all.
    setSavedContacts(uniqueContacts.length ? uniqueContacts : null);
    setContactModalOpen(false);
  };

  const handleCloseContactForm = () => {
    setContactModalOpen(false);
  };

  /* Inspection field only allow 0-999 */
  const setInspectionNumber = event => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
      else {
        const tempValue = event.target.value + event.key;
        if (Number(tempValue) >= 1000) {
          event.preventDefault();
        }
      }
  };

  const middleware = (submitFn: (data) => void) => data => {
    return submitFn({
      project: Number(selectedProjectId),
      name: data.name,
      description: data.description,
      gateCode: data.gateCode,
      folioId: data.folioId,
      permitCost: data.permitCost,
      serviceType: data.serviceType
        .map(el => el.id)
        .filter(el => el !== undefined),
      work: data.work.value
        ? +data.work.value
        : data.work.id,
      contractor: data.contractor?.id
        ? data.contractor.id
        : null,
      contacts: formatContacts(data.contacts),
      // Building is a project-level attribute — every permit under a project
      // shares it, so it's inherited here instead of being re-picked per permit.
      building: projectBuilding?.id || projectBuilding || null,
      manager: data.permitManager?.id,
      inspectionsCount: data.inspectionsNumber
        ? data.inspectionsNumber
        : 0,
      amount: data.totalContract ? data.totalContract : 0,
      expirationDate: data.expirationDate
        ? `${formatDate(
            data.expirationDate,
          )}`
        : null,
    });
  };

  return (
    <>
      <PermitTwoCol>
      <PermitCol>
      <InputBaseContainer>
        <Controller
          as={InputTextWithLabel}
          name="name"
          control={control}
          helperText={errors.name?.message}
          // @ts-ignore
          error={errors.name?.message}
          label={labels.name}
          isRequired={isYupFieldRequired(
            'name',
            validationSchema,
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          as={TextAreaWithLabel}
          name="description"
          control={control}
          error={errors.description}
          label={labels.description}
          isRequired={isYupFieldRequired(
            'description',
            validationSchema,
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          name="serviceType"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              isMulti
              selectAllActive
              placeholder=""
              label={labels.serviceType}
              helperText={errors.serviceType?.message}
              error={errors.serviceType}
              getOptionValue={(item: any) =>
                item.id || Math.random()
              }
              getOptionLabel={(item: any) =>
                item.name || item.title
              }
              isRequired={isYupFieldRequired(
                'serviceType',
                validationSchema,
              )}
              options={serviceTypes}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
          )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          name="work"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              placeholder=""
              menuPlacement="bottom"
              label={labels.work}
              helperText={errors.work?.message}
              error={errors.work}
              options={workTypes}
              getOptionValue={(item: any) =>
                item.id || Math.random()
              }
              getOptionLabel={(item: any) => item.title}
              isRequired={isYupFieldRequired(
                'work',
                validationSchema,
              )}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
          )}
        />
      </InputBaseContainer>
      </PermitCol>
      <PermitCol>
      <StyledInputContainer>
        <HiddenInput>
          <Controller
            name="contacts"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                menuPlacement="bottom"
                label=""
                helperText={errors.contacts?.message}
                error={errors.contacts}
                // @ts-ignore
                options={selectedContacts}
                getOptionValue={(item: any) =>
                  item?.id || Math.random()
                }
                getOptionLabel={(item: any) => item?.title}
                isRequired={isYupFieldRequired(
                  'contacts',
                  validationSchema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </HiddenInput>
        <ContactsWrapper>
          <ContactsHeader>
            <ContactsLabel>
              Contacts <span style={{ color: 'var(--danger-500)' }}>*</span>
            </ContactsLabel>
            <StyledButtonMain
              variant="outlined"
              onClick={() => setContactModalOpen(true)}
            >
              Add Contact
            </StyledButtonMain>
          </ContactsHeader>
          <ContactsTable
            contacts={savedContacts}
            setContacts={setSavedContacts}
            contactTypes={contactTypes}
            error={errors.contacts?.message}
          />
          {errors.contacts?.message && (
            <ErrorMessage>
              {errors.contacts?.message}
            </ErrorMessage>
          )}
        </ContactsWrapper>
      </StyledInputContainer>
      <InputBaseContainer>
        <Controller
          name="permitManager"
          defaultValue=""
          control={control}
          render={props => (
            <InputSelectWithLabel
              placeholder=""
              menuPlacement="bottom"
              label={labels.manager}
              helperText={errors.permitManager?.message}
              error={errors.permitManager}
              options={allManagers}
              getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
              getOptionLabel={(item: any) =>
                  [item?.firstName, item?.lastName]
                    .filter(Boolean)
                    .join(' ')
                }
              isRequired={isYupFieldRequired(
                  'permitManager',
                  validationSchema,
                )}
                // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            />
            )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          as={InputTextWithLabel}
          name="inspectionsNumber"
          control={control}
          helperText={errors.inspectionsNumber?.message}
          error={errors.inspectionsNumber}
          label={labels.inspections}
          onKeyPress={event => setInspectionNumber(event)}
          isRequired={isYupFieldRequired(
              'inspectionsNumber',
              validationSchema,
            )}
        />
      </InputBaseContainer>
      <InputBaseContainer>
        <Controller
          name="expirationDate"
          defaultValue=""
          valueName="selected"
          onChange={([selected]) => selected}
          control={control}
          menuPlacement="top"
          render={props => (
            <InputDatePickerWithLabel
              label={labels.expirationDate}
              error={errors.expirationDate}
              dateFormat={DEFAULT_DATE_FORMAT}
              isRequired={isYupFieldRequired(
                            'expirationDate',
                            validationSchema,
                          )}
              selected={props.value}
              {...props}
            />
                      )}
        />
      </InputBaseContainer>
      </PermitCol>
      </PermitTwoCol>

      <TwinControls>
        <InputBaseContainer>
          <Controller
            as={InputTextWithLabel}
            name="gateCode"
            control={control}
            label={labels.gateCode}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            as={InputTextWithLabel}
            name="folioId"
            control={control}
            label={labels.folioId}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            as={InputTextWithLabel}
            name="permitCost"
            control={control}
            label={labels.permitCost}
          />
        </InputBaseContainer>
      </TwinControls>

      <SaveButtonWrapper>
        <ButtonWithLoader
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSubmit(middleware(createOrUpdate))}
          disabled={
            formState.isSubmitting ||
            !formState.isDirty
          }
          loading={formState.isSubmitting}
        >
          {formState.isSubmitting ? 'Saving...' : 'Save'}
        </ButtonWithLoader>
      </SaveButtonWrapper>

      {isContactModalOpen && (
        <StyledContactModal
          title="Add Contacts"
          onClose={handleCloseContactForm}
          zIndex={20}
        >
          <ContactModalHeader>
            <ActionsPanel
              isBreadCrumbsVisible={false}
              onSearch={setSearch}
              isActionPanelMoreVisible={false}
              className="action-container"
            />
            <ButtonWithLoader
              variant="contained"
              color="primary"
              onClick={() => {
                handleCloseContactForm();
                setAddContactModal(true);
              }}
              loading={false}
            >
              New
            </ButtonWithLoader>
          </ContactModalHeader>

          {(!search || search.length < 2) && (
            <SearchHint>
              <SearchHintTitle>
                Start typing to find contacts
              </SearchHintTitle>
              <SearchHintBody>
                Enter at least <strong>2 characters</strong> in the
                search box above to look up contacts by name or email.
              </SearchHintBody>
            </SearchHint>
          )}

          {search && search.length >= 2 && (
            <ContactSearchList
              searchQuery={search}
              selectedIds={(selectedContacts || [])
                .map((el: any) => el?.id)
                .filter(Boolean)}
              onToggle={handleToggleContact}
            />
          )}

          <ContactModalFooter>
            <ButtonWithLoader
              variant="outlined"
              onClick={handleCloseContactForm}
              loading={false}
            >
              Cancel
            </ButtonWithLoader>
            <ButtonWithLoader
              variant="contained"
              color="primary"
              onClick={handleSaveContacts}
              loading={false}
            >
              Ok
            </ButtonWithLoader>
          </ContactModalFooter>
        </StyledContactModal>
      )}

      {isAddContactModal && (
        <ModalActionDialog
          title={addContactModalTitle}
          onCancel={() => {
            setAddContactModal(false);
            setAddContactModalTitle('Add Individual');
          }}
          actionFirst={null}
          actionSecond={null}
          // buttonFirstDisable={!selectedContacts}
          className="over"
          noCancel
        >
          <PermitAddContact
            setIsContactFormSubmitting={
              setIsContactFormSubmitting
            }
            setAddContactModal={setAddContactModal}
            setModalTitle={setAddContactModalTitle}
          />
        </ModalActionDialog>
      )}

      {modalError && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={close}
          errorMsg={modalErrorTitle}
        />
      )}
    </>
  );
};

interface Props {
  allContacts: any;
  permitStatus: any;
  serviceTypes: any;
  projectBuilding?: any;
  projectManager?: any;
  workTypes: any;
  contactTypes: any;
  managersData: any;
  setIsContactFormSubmitting?: (data) => void;
  setCreatedPermit: (data: boolean) => void;
  close: () => void;
  selectedProjectId: string;
}

const PermitTwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--space-3) var(--space-8);
  align-items: start;
`;

const PermitCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
`;

const TwinControls = styled.div`
 display: flex;
 flex-direction: row;
 gap: 16px;
`;

const SaveButtonWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  display: flex;
  justify-content: center;
`;

const StyledButtonMain = styled(ButtonMain)`
  &.MuiButton-root {
    line-height: 1.15;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 4px 0;
`;

const ContactsWrapper = styled.div`
  width: 100%;
`;

const ContactsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
`;

const ErrorMessage = styled.p`
  color: #f44336;
  font-size: 0.75em;
  margin: 3px 14px 0 14px;
`;

const HiddenInput = styled.div`
  width: 0;
  height: 0;
  opacity: 0;
  div {
    box-sizing: border-box;
    border: none;
  }
`;

const ContactsLabel = styled.p`
  font-family: ${fontFamily};
  font-style: normal;
  font-weight: normal;
  font-size: 1em;
  line-height: 21px;
  margin-top: 0;
`;

const StyledContactModal = styled(ModalDefaultDraggable)`
  text-align: left;
`;

const ContactModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);

  .action-container {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const ContactModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-4);
`;

const SearchHint = styled.div`
  text-align: center;
  padding: 32px 16px;
  margin-top: 12px;
  background: var(--neutral-50);
  border: 1px dashed var(--border);
  border-radius: 10px;
  color: var(--neutral-600);
`;

const SearchHintTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 6px;
`;

const SearchHintBody = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--neutral-600);

  strong {
    color: var(--mainText);
  }
`;

export default AddPermitForm;
